import React, { useState } from 'react';
import styled from 'styled-components';

const PortfolioSection = styled.section`
  padding: ${({ theme }) => `${theme.spacing['2xl']} 0`};
  background-color: ${({ theme }) => theme.colors.light};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  h2 {
    margin-bottom: 1rem;
  }
  
  p {
    font-size: 1.125rem;
    max-width: 600px;
    margin: 0 auto 2rem;
  }
`;

const FilterButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
`;

const FilterButton = styled.button<{ active: boolean }>`
  background: ${({ active, theme }) => 
    active ? theme.colors.black : 'transparent'};
  color: ${({ active, theme }) => 
    active ? 'white' : theme.colors.black};
  border: 2px solid ${({ theme }) => theme.colors.black};
  padding: 0.75rem 2rem;
  border-radius: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: ${({ theme }) => theme.colors.black};
    color: white;
    transform: translateY(-2px);
  }
`;

const PortfolioGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
`;

const ProjectCard = styled.div`
  background: white;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: ${({ theme }) => theme.transitions.default};
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    border-color: ${({ theme }) => theme.colors.accent};
  }
`;

const ProjectImage = styled.div<{ image: string }>`
  height: 250px;
  background: ${({ image }) => `url(${image}) center/cover no-repeat`};
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(37, 99, 235, 0.8) 0%, rgba(124, 58, 237, 0.8) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${ProjectCard}:hover &::after {
    opacity: 1;
  }
`;

const ProjectContent = styled.div`
  padding: 1.5rem;
`;

const ProjectCategory = styled.span`
  color: ${({ theme }) => theme.colors.accent};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 2px;
`;

const ProjectTitle = styled.h3`
  margin: 0.5rem 0 1rem;
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
`;

const ProjectDescription = styled.p`
  color: ${({ theme }) => theme.colors.gray};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ProjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
`;

const Tag = styled.span`
  background: ${({ theme }) => theme.colors.lightGray};
  color: ${({ theme }) => theme.colors.gray};
  padding: 0.25rem 0.75rem;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 0.875rem;
`;

const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const ProjectLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 500;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const projects = [
  {
    id: 1,
    title: 'Modern Dance - Taniec Serca',
    category: 'Modern',
    description: 'Nowoczesny taniec współczesny, gdzie każdy ruch opowiada historię. Dla wszystkich poziomów zaawansowania - od początkujących do średnio zaawansowanych.',
    image: 'https://images.unsplash.com/photo-1508700115892-45ecd05ae2ad?ixlib=rb-4.0.3&auto=format&fit=crop&w=1469&q=80',
    tags: ['Modern', 'Współczesny', 'Ekspresja', 'Wszystkie poziomy'],
    liveUrl: '#',
    codeUrl: '#'
  },
  {
    id: 2,
    title: 'High Heels - Pewność Siebie',
    category: 'High Heels',
    description: 'Taniec na obcasach, który buduje pewność siebie i kobiecość. Idealne połączenie elegancji i mocy dla początkujących.',
    image: 'https://images.unsplash.com/photo-1642157690261-d8b52223c9fa?q=80&w=987&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    tags: ['High Heels', 'Pewność siebie', 'Kobiecość', 'Początkujące'],
    liveUrl: '#',
    codeUrl: '#'
  },
  {
    id: 3,
    title: 'Akrobatyka - Siła i Gibkość',
    category: 'Akrobatyka',
    description: 'Zajęcia wzmacniające rozwijające siłę, stabilizację i gibkość. Podstawy akrobatyki dla bezpiecznego wykonywania elementów tanecznych.',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
    tags: ['Akrobatyka', 'Siła', 'Gibkość', 'Wzmacnianie'],
    liveUrl: '#',
    codeUrl: '#'
  },
  {
    id: 4,
    title: 'Pierwszy Taniec - Wasz Wyjątkowy Dzień',
    category: 'Pierwszy Taniec',
    description: 'Przygotowanie do najważniejszego tańca w waszym życiu. Indywidualne podejście i choreografia dopasowana do waszej osobowości.',
    image: 'https://images.unsplash.com/photo-1583939003579-730e3918a45a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80',
    tags: ['Pierwszy taniec', 'Ślub', 'Para', 'Indywidualne'],
    liveUrl: '#',
    codeUrl: '#'
  },
  {
    id: 5,
    title: 'Zajęcia dla Dzieci - Radość Ruchu',
    category: 'Dzieci',
    description: 'Taneczna zabawa dla najmłodszych, rozwijająca koordynację, rytm i kreatywność w przyjaznej atmosferze.',
    image: 'https://images.unsplash.com/photo-1503454537195-1dcabb73ffb9?ixlib=rb-4.0.3&auto=format&fit=crop&w=1486&q=80',
    tags: ['Dzieci', 'Zabawa', 'Koordynacja', 'Kreatywność'],
    liveUrl: '#',
    codeUrl: '#'
  },
  {
    id: 6,
    title: 'Zajęcia dla Dorosłych - Nigdy Nie Jest Za Późno',
    category: 'Dorośli',
    description: 'Taniec to pasja bez granic wiekowych. Zajęcia dostosowane do potrzeb dorosłych w komfortowej atmosferze.',
    image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1375&q=80',
    tags: ['Dorośli', 'Początkujący', 'Komfort', 'Pasja'],
    liveUrl: '#',
    codeUrl: '#'
  }
];

const categories = ['Wszystkie', 'Modern', 'High Heels', 'Akrobatyka', 'Pierwszy Taniec', 'Dzieci', 'Dorośli'];

const Portfolio = () => {
  const [activeFilter, setActiveFilter] = useState('Wszystkie');

  const filteredProjects = activeFilter === 'Wszystkie' 
    ? projects 
    : projects.filter(project => project.category === activeFilter);

  return (
    <PortfolioSection id="portfolio" className="section">
      <div className="container">
        <SectionHeader>
          <h2>Miejsce, gdzie tańczy się sercem 🖤</h2>
          <p>
            📍 Zielona Góra | Zajęcia taneczne dla dzieci, młodzieży i dorosłych<br/>
            Modern • Akrobatyka • High Heels • Pierwszy Taniec
          </p>
          <FilterButtons>
            {categories.map(category => (
              <FilterButton
                key={category}
                active={activeFilter === category}
                onClick={() => setActiveFilter(category)}
              >
                {category}
              </FilterButton>
            ))}
          </FilterButtons>
        </SectionHeader>
        
        <PortfolioGrid>
          {filteredProjects.map(project => (
            <ProjectCard key={project.id}>
              <ProjectImage image={project.image} />
              <ProjectContent>
                <ProjectCategory>{project.category}</ProjectCategory>
                <ProjectTitle>{project.title}</ProjectTitle>
                <ProjectDescription>{project.description}</ProjectDescription>
                <ProjectTags>
                  {project.tags.map(tag => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </ProjectTags>
                <ProjectLinks>
                  <ProjectLink href={project.liveUrl}>Zobacz Galerię</ProjectLink>
                  <ProjectLink href={project.codeUrl}>Więcej Info</ProjectLink>
                </ProjectLinks>
              </ProjectContent>
            </ProjectCard>
          ))}
        </PortfolioGrid>
      </div>
    </PortfolioSection>
  );
};

export default Portfolio;
